// ════════════════════════════════════════════════════════════════════════════
//  useBoletos.ts  —  Hooks para gestão de boletos
// ════════════════════════════════════════════════════════════════════════════

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { boletosService, type ListarBoletosParams } from '../services/boletosService';
import type { EmitirBoletoRequest, BaixarBoletoRequest } from '../types';

const KEYS = {
  list:   (p: ListarBoletosParams) => ['boletos', p] as const,
  detail: (id: number)             => ['boletos', id] as const,
};

export const useBoletos = (params: ListarBoletosParams = {}) => {
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: KEYS.list(params),
    queryFn:  () => boletosService.listar(params),
  });

  const emitir = useMutation({
    mutationFn: (req: EmitirBoletoRequest) => boletosService.emitir(req),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['boletos'] }); },
  });

  const baixar = useMutation({
    mutationFn: ({ id, req }: { id: number; req: BaixarBoletoRequest }) =>
      boletosService.baixar(id, req),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['boletos'] }); },
  });

  const cancelar = useMutation({
    mutationFn: (id: number) => boletosService.cancelar(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['boletos'] }); },
  });

  const marcarImpresso = useMutation({
    mutationFn: (id: number) => boletosService.marcarImpresso(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['boletos'] }); },
  });

  return {
    boletos:        query.data?.itens ?? [],
    total:          query.data?.total ?? 0,
    totalPaginas:   query.data?.totalPaginas ?? 0,
    isLoading:      query.isLoading,
    isFetching:     query.isFetching,
    error:          query.error,
    emitir:         emitir.mutateAsync,
    isEmitindo:     emitir.isPending,
    baixar:         baixar.mutateAsync,
    isBaixando:     baixar.isPending,
    cancelar:       cancelar.mutateAsync,
    isCancelando:   cancelar.isPending,
    marcarImpresso: marcarImpresso.mutateAsync,
  };
};
